import { Component, ElementRef, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import * as XLSX from 'xlsx';
import { HttpErrorResponse } from '@angular/common/http';
import { DatasetService } from '../../../../../shared/dataset/service/dataset.service';
import { StudyEditorContext } from '../../../../study-editor.context';
import { AlertService } from '../../../../../shared/alert/alert.service';
import { ImportStudyContext } from '../../../dataset/import-study/import-study-context';
import { ModalConfirmComponent } from '../../../../../shared/modal/modal-confirm.component';
import { VariableTypeEnum } from '../../../../../shared/ontology/variable-type.enum';
import { ImportStudyMapComponent } from '../../../dataset/import-study/import-study-map/import-study-map.component';
import { ExperimentalDesignContext } from '../../experimental-design-context';
import { ALLOWED_ADDITIONAL_SYSTEM_VARIABLES } from '../../../../../app.constants';
import { JhiEventManager } from '../../../../../shared/core-utils';

interface ImportFormat {
    name: string;
    extension: string;
    isVisible: boolean;
}


@Component({
    selector: 'jhi-import-additional-design-details',
    templateUrl: './import-additional-design-details.component.html'
})
export class ImportAdditionalDesignDetailsComponent {

    @ViewChild('fileUpload') fileUpload!: ElementRef;

    title: string;

    datasetId: number;

    file: File | null = null;
    importedData: any[] | null = null;
    isTransposed = false;

    selectedFormat: ImportFormat | null = null;

    private destroy$ = new Subject<void>();
    isSaving: boolean;

    ALLOWED_ADDITIONAL_SYSTEM_VARIABLES = ALLOWED_ADDITIONAL_SYSTEM_VARIABLES;
    importFormats: ImportFormat[] = [];

    constructor(
        public modalService: NgbModal,
        public activeModal: NgbActiveModal,
        private datasetService: DatasetService,
        private studyEditorContext: StudyEditorContext,
        private alertService: AlertService,
        private importStudyContext: ImportStudyContext,
        private experimentalDesignContext: ExperimentalDesignContext,
        private eventManager: JhiEventManager,
        private translateService: TranslateService
    ) {
    }

    ngOnInit(): void {
        this.title = this.translateService.instant('study-editor.experiment-design.additional-details.import');

        this.initImportFormats();
        this.selectedFormat = this.importFormats[1];
    }

    initImportFormats(): void {
        this.importFormats = [
            { name: 'CSV', extension: '.csv', isVisible: true },
            { name: 'Excel', extension: '.xls,.xlsx', isVisible: true },
            { name: 'Fieldbook CSV', extension: '.csv', isVisible: true },
            { name: 'Fieldbook Excel', extension: '.xls,.xlsx', isVisible: true  }
        ];
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    cancel(): void {
        this.activeModal.dismiss();
    }

    clearSelectedFile(): void {
        this.file = null;
        this.importedData = null;
    }

    submitImport(): void {

        this.isSaving = true;

        this.validateNewVariables().then(result => {
            if (result.length > 0) {
                this.showAddVariableConfirmModal(result, this.datasetId);
            } else {
                this.importObservations(true);
            }
        });
    }

    async validateNewVariables(): Promise<string[]> {
        try {
            const projectProperties = await this.datasetService.getAllProperties(this.studyEditorContext.studyId, this.datasetId).toPromise();
            const importedData = this.importedData![0];
            const existingVariableNames: string[] = [];
            const existingVariableAliases: string[] = [];
            const existingNameTypes: string[] = [];

            projectProperties.variables.forEach(e => {
                existingVariableAliases.push(e.alias);
                existingVariableNames.push(e.name);
            });

            projectProperties.nameTypes.forEach(e => {
                existingNameTypes.push(e.code);
            });

            return  importedData
                .map(value => value.trim())
                .filter(variableName =>
                    variableName !== '' &&
                    !existingVariableAliases.includes(variableName) &&
                    !existingVariableNames.includes(variableName) &&
                    !existingNameTypes.includes(variableName)
                );
        } catch (error) {
            this.isSaving = false;
            this.alertService.error('study-editor.experiment-design.additional-details.import.variable.validation.error');
            return [];
        }
    }

    importObservations(processWarnings: boolean): void {
        const optionalVariables = this.experimentalDesignContext.optionalDesignVariables.filter(
            (item) => item.deletable || //
            this.ALLOWED_ADDITIONAL_SYSTEM_VARIABLES.includes(item.termId)
        );

        this.datasetService.importAdditionalDesignFactors(this.studyEditorContext.studyId,
            this.datasetId,
            this.importedData,
            processWarnings,
            optionalVariables
        ).toPromise().then((response) => {
            this.isSaving = false;
            this.alertService.success('study-editor.study-dataset.import-study-modal.import.design-details.success');
            this.activeModal.close('success');
            this.eventManager.broadcast({ name: 'updateAdditionalDetails'});
        }, (response: HttpErrorResponse) => {
            this.isSaving = false;
            if (response.status === 400) {
                this.alertService.error('error.custom', { param: response.error.errors[0].message });
            } else if (response.status === 412) {
                this.showConfirmModal(response.error.errors);
            } else {
                this.alertService.error('error.general');
            }
        });
    }

    close(): void {
        this.activeModal.close();
    }

    showConfirmModal(warnings: any[]): void {
        this.activeModal.close();
        const warningMessages = warnings.map(warning => warning.message);

        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message =
            this.translateService.instant('study-editor.study-dataset.import-study-modal.import.observation.warning', { param: warningMessages });

        confirmModalRef.result.then(() => {
            this.importObservations(false);
        });
    }

    showDesignMapPopup(result: string[], datasetId: number): void {
        this.importStudyContext.datasetId = datasetId;
        this.importStudyContext.importedData = this.importedData;
        this.importStudyContext.variableTypeIds = [VariableTypeEnum.EXPERIMENTAL_DESIGN];
        this.importStudyContext.isDesignDetailsImportBoolean = true;

        // Ignore blank headers before rebuilding the unmapped items list.
        this.importStudyContext.unmappedItems = result.filter(value => value?.trim() !== '')
            .map((value, index) => {
            return {
                id: String(index),
                name: value
            };
        });
        const modalRef = this.modalService.open(ImportStudyMapComponent as Component, { size: 'xl', backdrop: 'static' });
        modalRef.result.then(() => {
            this.importObservations(true);
        });
    }

    showAddVariableConfirmModal(result: string[], datasetId: number): void {
        this.activeModal.close();

        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.showButtonIcons = false;
        confirmModalRef.componentInstance.cancelLabel = this.translateService.instant('no');
        confirmModalRef.componentInstance.confirmLabel = this.translateService.instant('yes');
        confirmModalRef.componentInstance.message = this.translateService.instant('study-editor.study-dataset.import-study-modal.import.confirm-mapping');

        confirmModalRef.result.then(() => {
            this.showDesignMapPopup(result, datasetId);
        }, () => {
            this.importObservations(true);
        });
    }

    handleFileChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        if (!target.files?.length) {
            return;
        }

        const file = target.files[0];
        const reader = new FileReader();

        reader.onload = (e: any) => {
            try {
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: 'binary', raw: true });

                // grab first sheet or the "Observation" sheet
                let wsname = wb.SheetNames[0];

                if (wb.SheetNames.length > 1) {
                    wsname = 'Observation';

                    if (!wb.Sheets[wsname]) {
                        this.alertService.error('study-editor.study-dataset.import-study-modal.import.wrong-observation-sheet-name');
                        return;
                    }
                }

                // Check if the file is transposed
                const workbook = XLSX.read(bstr, { type: 'binary' });
                let parseData;
                let isTransposed = false;

                if (wsname === 'Observation' && this.isImportedFileTransposed(workbook)) {
                    parseData = this.parseFile(workbook, wsname);
                    isTransposed = true;

                    const result = this.reverseTransposedData(parseData);
                    if (!result.success) {
                        this.alertService.error('error.custom', { param: result.errorMessage || this.translateService.instant('study-editor.study-dataset.import-study-modal.import.error.transpose-file') });
                        return;
                    }
                    parseData = result.data;
                } else {
                    parseData = this.parseFile(wb, wsname);
                }

                // update component state
                this.importedData = parseData;
                this.file = file;
                this.isTransposed = isTransposed;

                // Truncate filename if too long
                const length = 20;
                if (this.file && this.file.name.length > length) {
                    (this.file as any).abbrName = this.file.name.substring(0, length) + '...';
                } else if (this.file) {
                    (this.file as any).abbrName = this.file.name;
                }
            } catch (error) {
                this.alertService.error('study-editor.study-dataset.import-study-modal.import.error.reading-file');
                console.error(error);
            }
        };

        reader.readAsBinaryString(file);
    }

    private parseFile(wb: XLSX.WorkBook, wsname: string): any[] {
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
        return data.map((row: any[]) =>
            row.map((cell: any) => this.stripHtmlTags(cell?.toString() || ''))
        );
    }

    private stripHtmlTags(str: string): string {
        // Remove Tag HTML
        str = str.replace(/<\/?[^>]+(>|$)/g, '');
        // Remove special characters: <, >, ?, % |
        return str.replace(/[<>?%|]/g, '');
    }

    private isImportedFileTransposed(wb: XLSX.WorkBook): boolean {
        const worksheet = wb.Sheets['Observation'];
        return !!worksheet['!merges'];
    }

    private reverseTransposedData(parsedData: any[]): { success: boolean; data?: any[]; errorMessage?: string } {
        if (parsedData.length < 2) {
            return {
                success: false,
                errorMessage: this.translateService.instant('study-editor.study-dataset.import-study-modal.import.error.invalid-transpose-file')
            };
        }

        try {
            const reversedData: any[] = [];
            const observationUnitVarIndex = parsedData[1].findIndex((element: any) =>
                element !== null && element !== '') - 1;

            reversedData.push(this.parseHeaders(parsedData, observationUnitVarIndex));
            this.parseObservations(reversedData, parsedData, observationUnitVarIndex);

            return { success: true, data: reversedData };
        } catch (error) {
            return {
                success: false,
                errorMessage: this.translateService.instant('study-editor.study-dataset.import-study-modal.import.error.transpose-file')
            };
        }
    }

    private parseHeaders(parsedData: any[], observationUnitVarIndex: number): any[] {
        const headers: any[] = [];
        headers.push(...parsedData[0].slice(0, observationUnitVarIndex + 1));
        const nonFactorHeaders = this.getNonFactorHeaders(parsedData);
        if (nonFactorHeaders.length > 0) {
            headers.push(...nonFactorHeaders);
        }
        return headers;
    }

    private parseObservations(reversedData: any[], parsedData: any[], observationUnitVarIndex: number): void {
        const subObservationsPerPlot = this.getSubObservationsPerPlot(parsedData[0]);
        const numberOfSubObsPerPlot = parseInt(subObservationsPerPlot, 10);
        const numberOfObservations = parsedData.length;
        const numberOfNonFactors = this.getNonFactorHeaders(parsedData).length;

        for (let i = 2; i < numberOfObservations; i++) {
            const factorsValues = parsedData[i].slice(0, observationUnitVarIndex);
            for (let observationUnitValue = 1; observationUnitValue <= numberOfSubObsPerPlot; observationUnitValue++) {
                const observationDataRow = [...factorsValues];
                observationDataRow.push(observationUnitValue.toString());

                if (numberOfNonFactors > 0) {
                    const startOfValuesForSubOs = observationUnitVarIndex + 1 + ((observationUnitValue - 1) * numberOfNonFactors);
                    const endOfValuesForSubOs = numberOfNonFactors + startOfValuesForSubOs;
                    const nonFactorValues = parsedData[i].slice(startOfValuesForSubOs, endOfValuesForSubOs);
                    observationDataRow.push(...nonFactorValues);
                }

                reversedData.push(observationDataRow);
            }
        }
    }

    private getNonFactorHeaders(parsedData: any[]): any[] {
        return parsedData[1].filter((element: any, index: number, array: any[]) => {
            return element !== '' && array.indexOf(element) === index;
        });
    }

    private getSubObservationsPerPlot(headers: any[]): string {
        for (let i = headers.length - 1; i >= 0; i--) {
            if (headers[i] !== null && headers[i] !== '') {
                return headers[i];
            }
        }
        return '1'; // Default to 1 if not found
    }

}
